import React, { useState } from "react";
import { motion } from "framer-motion";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Lock, Mail } from "lucide-react";
import keiretsuLogo from "@/assets/keiretsu-forum-logo.png";
import { getSession, saveSessionFromToken, clearSession } from "@/utils/sessionStorage";

interface CampaignUnlockFormProps {
  campaignCode: string;
  onUnlock: (session: CampaignSession) => void;
}

export interface CampaignSession {
  email: string;
  campaign_code: string;
  campaign_name: string;
  unlocked_at: string;
  expires_at: string;
  is_admin?: boolean;
  remember_me?: boolean;
}

const STORAGE_KEY = "campaign_session";

export const getCampaignSession = (): CampaignSession | null => {
  const session = getSession<CampaignSession>(STORAGE_KEY);
  if (!session) return null;
  return session;
};

export const saveCampaignSession = (token: string, rememberMe: boolean = false): CampaignSession | null => {
  const session = saveSessionFromToken<CampaignSession>(STORAGE_KEY, token, rememberMe);
  if (!session) return null;
  return session;
};

export const clearCampaignSession = () => {
  clearSession(STORAGE_KEY);
};

const CampaignUnlockForm: React.FC<CampaignUnlockFormProps> = ({ campaignCode, onUnlock }) => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // First, try admin authentication
      const { data: adminData, error: adminError } = await supabase.functions.invoke('verify-campaign-password', {
        body: {
          email: email.trim(),
          campaign_code: '', // Empty for admin check
          password: password
        }
      });

      if (!adminError && adminData?.success && adminData?.is_admin) {
        // Admin access granted - save session and unlock
        const session = saveCampaignSession(adminData.session_token, rememberMe);
        
        if (session) {
          toast({
            title: "Admin Access Granted",
            description: "Welcome! You have admin access to the investment terms."
          });
          onUnlock(session);
        }
        return;
      }

      // Not an admin, try campaign password
      const { data, error } = await supabase.functions.invoke('verify-campaign-password', {
        body: {
          email: email.trim(),
          campaign_code: campaignCode,
          password: password
        }
      });

      if (error || !data?.success) {
        toast({
          title: "Access Denied",
          description: data?.error || "Incorrect email or password. Please try again.",
          variant: "destructive"
        });
        return;
      }

      // Save session to appropriate storage based on rememberMe
      const session = saveCampaignSession(data.session_token, rememberMe);
      
      if (session) {
        toast({
          title: "Access Granted",
          description: `Welcome! You now have access to the ${session.campaign_name} investment terms.`
        });
        onUnlock(session);
      }

    } catch (error) {
      console.error("Unlock error:", error);
      toast({
        title: "Error",
        description: "Something went wrong. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="border-2 border-black rounded-xl overflow-hidden"
    >
      {/* KF Logo Header */}
      <div className="bg-white flex justify-center py-6">
        <img 
          src={keiretsuLogo} 
          alt="Keiretsu Forum Northwest & Rockies" 
          className="h-32 object-contain" 
        />
      </div>

      {/* Unlock Form */}
      <div className="bg-gradient-to-br from-gray-50 to-blue-50 border-t-2 border-black p-8">
        <div className="max-w-md mx-auto text-center">
          <div className="mb-6">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-gray-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Exclusive Investment Terms
            </h2>
            <p className="text-gray-600">
              Enter your credentials to view the Keiretsu Forum investment terms and submit your investment request.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="text-left">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 w-full"
                  placeholder="your@email.com"
                  autoComplete="off"
                />
              </div>
            </div>

            <div className="text-left">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Access Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 w-full"
                  placeholder="Enter access password"
                  autoComplete="off"
                />
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="rememberMe"
                checked={rememberMe}
                onCheckedChange={(checked) => setRememberMe(checked === true)}
              />
              <label
                htmlFor="rememberMe"
                className="text-sm font-medium text-gray-700 cursor-pointer"
              >
                Remember me for 10 days
              </label>
            </div>
            
            {rememberMe && (
              <p className="text-xs text-gray-500">
                Your session will persist on this device
              </p>
            )}

            <Button
              type="submit"
              size="lg"
              disabled={isSubmitting}
              className="w-full bg-black hover:bg-gray-800 text-white font-semibold py-3"
            >
              {isSubmitting ? "Verifying..." : "View Investment Terms"}
            </Button>
          </form>

          <p className="mt-6 text-sm text-gray-500">
            Don't have access? Contact your Keiretsu Forum chapter representative.
          </p>
        </div>
      </div>
    </motion.div>
  );
};

export default CampaignUnlockForm;
